import axios from "axios";
import { getAuthToken, getOrCreateDeviceId } from "@/features/auth/api/token";

const apiClient = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || "https://api.travelhug.ai/",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
});

// Always attach X-Device-Id and Authorization (except when getting the token)
apiClient.interceptors.request.use(async (config) => {
    const deviceId = getOrCreateDeviceId();
    config.headers["X-Device-Id"] = deviceId; // <-- REQUIRED on ALL calls

    if (!config.url?.includes("/api/v1/auth/token") && !config.url?.includes("/api/v1/auth/guest-login")) {
        const token = await getAuthToken();
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Refresh token logic
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (token: string) => void;
    reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token as string);
        }
    });

    failedQueue = [];
};

apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers["Authorization"] = "Bearer " + token;
                        return apiClient(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Import refreshAuthToken dynamically from token.ts
                const { refreshAuthToken, clearAuthToken } = await import("@/features/auth/api/token");

                const newToken = await refreshAuthToken();

                if (newToken) {
                    isRefreshing = false;
                    processQueue(null, newToken);

                    // Update header for this request
                    originalRequest.headers["Authorization"] = "Bearer " + newToken;
                    return apiClient(originalRequest);
                } else {
                    // Refresh failed
                    isRefreshing = false;
                    processQueue(new Error("Failed to refresh token"), null);
                    clearAuthToken();
                    window.location.href = "/auth/login";
                    return Promise.reject(error);
                }
            } catch (refreshError) {
                isRefreshing = false;
                processQueue(refreshError, null);
                // clearAuthToken(); // Already handled above?
                window.location.href = "/auth/login";
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);


export default apiClient;
